import api from './api'
import { http } from './http'

export type PaginatedResponse<T> = {
  results: T[]
  count: number
  next: string | null
  previous: string | null
}

export type AuthTokenPair = {
  access: string
  refresh: string
}

export type SignupPayload = {
  username: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  password: string
  repeatPassword: string
  locale?: string
  timezone?: string
  is_active?: boolean
}

export type BudgetUser = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  phone: string
  is_superuser: boolean
  is_staff: boolean
  locale: string
  timezone: string
}

export type User = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  phone: string
  is_superuser: boolean
  is_active: boolean
  locale: string
  timezone: string
  date_joined: string
  last_login: string | null
}

export type CategoryType = 'income' | 'expense'

export type Category = {
  id: number
  user: number
  name: string
  type: CategoryType
  icon: string
  color: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export type Transaction = {
  id: number
  user: number
  category: Category
  amount: string
  description: string
  transaction_date: string
  payment_method: string
  receipt_photo: string | null
  is_recurring: boolean
  type: CategoryType
  created_at: string
  updated_at: string
}

export type Budget = {
  id: number
  user: number
  category: Category
  monthly_limit: string
  created_at: string
  updated_at: string
}

export type Notification = {
  id: number
  user: number
  title: string
  message: string
  is_read: boolean
  created_at: string
  updated_at: string
}

export type ReportsSummary = {
  total_income: number
  total_expense: number
  balance: number
  budgets_count: number
  budgets_exceeded: number
  transactions_count: number
}

export type MonthlySummary = {
  income: number
  expense: number
  balance: number
}
export type CategorySummary = {
  category_id: number
  category_name: string
  category_type: CategoryType
  category_icon: string
  category_color: string
  income: string
  expense: string
  percentage: string
}

export type BudgetStatus = {
  budget_id: number
  category_name: string
  monthly_limit: number
  spent_this_month: number
  percentage_used: number
  is_exceeded: boolean
  remaining: number
}

export type ChangePasswordPayload = {
  current_password: string
  new_password: string
  new_password_confirm: string
}

export type UpdateProfilePayload = {
  first_name?: string
  last_name?: string
  email?: string
  locale?: string
  timezone?: string
}

export type SyncTransaction = {
  id?: number | null
  category_id: number
  amount: string
  description: string
  transaction_date: string
  payment_method: string
  type: CategoryType
  is_recurring: boolean
  receipt_photo?: string | null
}

export type SyncPayload = {
  transactions: SyncTransaction[]
  deleted_transaction_ids: number[]
}

export type SyncResponse = {
  created_count: number
  updated_count: number
  deleted_count: number
  failed_count: number
  errors: string[]
}

export const budgetApi = {
  health: () => http<{ status: string; service: string }>(api.health()),
  login: (payload: { username: string; password: string }) =>
    http<AuthTokenPair>(api.authLogin(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  signup: (payload: SignupPayload) =>
    http<{ message: string }>(api.authSignup(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  me: () => http<BudgetUser>(api.authMe()),
  updateProfile: (payload: UpdateProfilePayload) =>
    http<BudgetUser>(api.authMe(), {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  changePassword: (payload: ChangePasswordPayload) =>
    http<{ message: string }>(api.authChangePassword(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  categories: (page?: number) => http<any>(`${api.categories()}${page ? `?page=${page}` : ''}`),
  createCategory: (payload: Omit<Category, 'id' | 'user' | 'created_at' | 'updated_at'>) =>
    http<Category>(api.categories(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCategory: (id: number, payload: Omit<Category, 'id' | 'user' | 'created_at' | 'updated_at'>) =>
    http<Category>(api.category(String(id)), {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteCategory: (id: number) =>
    http<void>(api.category(String(id)), {
      method: 'DELETE',
    }),
  transactions: (page?: number) => http<any>(`${api.transactions()}${page ? `?page=${page}` : ''}`),
  createTransaction: (payload: any) =>
    http<Transaction>(api.transactions(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateTransaction: (id: number, payload: Omit<Transaction, 'id' | 'user' | 'created_at' | 'updated_at'>) =>
    http<Transaction>(api.transaction(String(id)), {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteTransaction: (id: number) =>
    http<void>(api.transaction(String(id)), {
      method: 'DELETE',
    }),
  processRecurringTransactions: () =>
    http<{ created_count: number; message: string }>(api.transactionsProcessRecurring(), {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  budgets: (page?: number) => http<any>(`${api.budgets()}${page ? `?page=${page}` : ''}`),
  createBudget: (payload: any) =>
    http<Budget>(api.budgets(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateBudget: (id: number, payload: Omit<Budget, 'id' | 'user' | 'created_at' | 'updated_at'>) =>
    http<Budget>(api.budget(String(id)), {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteBudget: (id: number) =>
    http<void>(api.budget(String(id)), {
      method: 'DELETE',
    }),
  notifications: () => http<any>(api.notifications()),
  users: () => http<any>(api.allUsers()),
  createUser: (payload: Omit<User, 'id' | 'date_joined' | 'last_login'>) =>
    http<User>(api.allUsers(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateUser: (id: number, payload: Partial<Omit<User, 'id' | 'date_joined'>>) =>
    http<User>(api.user(String(id)), {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteUser: (id: number) =>
    http<void>(api.user(String(id)), {
      method: 'DELETE',
    }),
  updatePassword: (payload: { current_password: string; new_password: string; new_password_confirm: string }) =>
    http<{ detail: string }>(api.changePassword(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getReportsSummary: (year: number, month: number) =>
    http<ReportsSummary>(`${api.reportsSummary()}?year=${year}&month=${month}`),
  getReportsMonthly: (year: number, month: number) =>
    http<MonthlySummary>(`${api.reportsMonthly()}?year=${year}&month=${month}`),
  getReportsByCategory: (year: number, month: number) =>
    http<CategorySummary[]>(`${api.reportsByCategory()}?year=${year}&month=${month}`),
  getReportsBudgetsStatus: (year: number, month: number) =>
    http<BudgetStatus[]>(`${api.reportsBudgetsStatus()}?year=${year}&month=${month}`),
  getReportsDaily: () => http<MonthlySummary>(api.reportsDaily()),
  getReportsWeekly: () => http<MonthlySummary>(api.reportsWeekly()),
  exportTransactions: (format: 'pdf' | 'xlsx', startDate?: string, endDate?: string) => {
    const url = new URL(api.reportsExport())
    url.searchParams.append('export_format', format)
    if (startDate) url.searchParams.append('start_date', startDate)
    if (endDate) url.searchParams.append('end_date', endDate)
    return fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('budget-quotidien-access-token') || ''}`,
      },
    })
  },
  syncData: (payload: SyncPayload) =>
    http<SyncResponse>(api.sync(), {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}
